/// <reference types="vitest" />
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  GoogleAuthProvider,
  sendSignInLinkToEmail,
  signInWithPopup,
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  User,
  UserCredential,
  onIdTokenChanged,
} from "firebase/auth";

import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "integrations/firebase/auth";
import { GlobalLoading } from "components/loading";
import { logError } from "shared/services/ErrorReporting";
import { jwtDecode } from "jwt-decode";
import { t } from "i18next";
import { toast } from "@/utils/toast";

export interface IAuthContext {
  authUser?: User | null;
  loading: boolean;
  error?: Error;
  isLoggedIn: boolean;
  authToken?: string;

  signInWithOauth: (type: OAuthProviderType) => Promise<UserCredential | void>;

  signInWithEmailAndPassword: (
    email: string,
    password: string,
  ) => Promise<UserCredential | void>;

  sendLoginLink: (email: string) => Promise<void>;

  signOut: () => Promise<void>;
}

export const AuthContext = createContext<IAuthContext>({
  isLoggedIn: false,
  loading: true,
  sendLoginLink: async () => {},
  signInWithOauth: async () => {},
  signInWithEmailAndPassword: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

const OAUTH_PROVIDERS = {
  google: new GoogleAuthProvider(),
};

type OAuthProviderType = keyof typeof OAUTH_PROVIDERS;

export interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
  userId: string;
  photoUrl?: string;
}

export const AuthContextProvider = ({ children }: PropsWithChildren) => {
  const [authToken, setAuthToken] = useState<string>();
  const [initialized, setInitialized] = useState(false);
  const [authUser, loading] = useAuthState(auth);
  const [error, setError] = useState<Error>();

  const signOut = useCallback(() => auth.signOut(), []);

  useEffect(() => {
    if (loading) {
      return;
    }

    setInitialized(true);
  }, [authUser, loading]);

  useEffect(() => {
    if (error) {
      logError(error);
      toast.error(t("login:error.loginFailed"));
      setError(undefined);
    }
  }, [error]);

  const signInWithOauth = useCallback(async (type: OAuthProviderType) => {
    const provider = OAUTH_PROVIDERS[type];

    if (!provider) {
      throw new Error(`No provider found for type ${type}`);
    }

    try {
      const result = await signInWithPopup(auth, provider);
      toast.success(t("login:success.toast.title"));
      return result;
    } catch (e) {
      setError(e as Error);
    }
  }, []);

  const signInWithEmailAndPassword = useCallback(
    async (email: string, password: string) => {
      try {
        const result = await firebaseSignInWithEmailAndPassword(
          auth,
          email,
          password,
        );
        toast.success(t("login:success.toast.title"));
        return result;
      } catch (e) {
        setError(e as Error);
      }
    },
    [],
  );

  const sendLoginLink = useCallback(async (email: string) => {
    try {
      window.localStorage.setItem("emailForSignIn", email);

      await sendSignInLinkToEmail(auth, email, {
        handleCodeInApp: true,
        url: `${window.location.origin}/login-link`,
      });

      toast.success(t("login:link.success.linkSent", { email }));
    } catch (e) {
      setError(e);
      window.localStorage.removeItem("emailForSignIn");
    }
  }, []);

  useEffect(() => {
    if (!authUser) {
      setAuthToken(undefined);
      return;
    }

    const updateToken = async (user: User, forceUpdate: boolean = false) => {
      try {
        const token = await user.getIdToken(forceUpdate);
        setAuthToken(token);
        return token;
      } catch (err) {
        logError(err);
        setAuthToken(undefined);
      }
    };

    let refreshTimeout: NodeJS.Timeout;
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (user) {
        const token = await updateToken(user, true);
        if (!token) return;

        const decodedToken = jwtDecode(token);
        if (!decodedToken.exp) {
          logError("Token decoding failed.");
          return;
        }
        const expirationTime = decodedToken.exp * 1000;
        const timeToRefresh = expirationTime - Date.now() - 5 * 60 * 1000;

        refreshTimeout = setTimeout(() => {
          updateToken(user, true);
        }, timeToRefresh);
      } else {
        setAuthToken(undefined);
      }
    });

    return () => {
      unsubscribe();
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
    };
  }, [authUser]);

  const isLoggedIn = !!authUser;
  const authProviderValue = useMemo(
    () => ({
      authUser,
      loading,
      error,
      isLoggedIn,
      signInWithOauth,
      signInWithEmailAndPassword,
      signOut,
      sendLoginLink,
      authToken,
    }),
    [
      authUser,
      loading,
      error,
      isLoggedIn,
      signInWithOauth,
      signInWithEmailAndPassword,
      signOut,
      sendLoginLink,
      authToken,
    ],
  );

  if (loading || !initialized) {
    return <GlobalLoading />;
  }

  return <AuthContext value={authProviderValue}>{children}</AuthContext>;
};
